import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { apiErrorMessage, decideTermOut, getFacilityPhase, getTermOutRequests, requestTermOut, type TermOutRequestRow } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { FacilityPhase } from '../types/facilityWorkspace';
import { fmtDate } from '../presentation/format';
import { phaseDates, phaseLabel } from './FacilityPhaseBadge';

/**
 * Work plan item F step 5: the facility's structure (phase, revolving period, amortisation start) and
 * the governed term-out. A maker requests the term-out with its effective date and rationale; a
 * different checker approves or rejects. Approval ends the revolving period on that date: no new
 * draws, the unused fee stops, the facility amortises to maturity.
 */

interface Props {
  facilityId: number;
  canMake: boolean;
  canCheck: boolean;
  currentUser?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const Card = styled.section`
  margin-top: 12px; padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; margin-top: .8rem; }
  .fact { padding: .5rem .6rem; border: 1px solid rgba(148,163,184,.12); border-radius: 10px; background: rgba(5,12,24,.4); }
  .fact .label { color: #94a8c2; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
  .fact .value { margin-top: 2px; color: #f8fafc; font-size: 15px; font-weight: 650; }
  input, textarea { min-height: 34px; padding: .35rem .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 8px; background: #07101f; color: #e2e8f0; font-size: 13px; font: inherit; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  button.primary { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.25); }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; }
  th { text-align: left; padding: .4rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .4rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; vertical-align: top; }
  .muted { color: #94a3b8; } .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
`;

const words = (v: unknown) => String(v ?? '').toLowerCase().replace(/_/g, ' ');
const date = (v: unknown) => (v ? fmtDate(String(v)) : '—');

const TermOutPanel: React.FC<Props> = ({ facilityId, canMake, canCheck, currentUser, onFeedback }) => {
  const [phase, setPhase] = useState<FacilityPhase | null>(null);
  const [rows, setRows] = useState<TermOutRequestRow[]>([]);
  const [open, setOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [notes, setNotes] = useState('');
  const [clause, setClause] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([getFacilityPhase(facilityId), getTermOutRequests(facilityId)]);
      setPhase(p); setRows(r);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The facility structure could not be loaded.') });
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await requestTermOut(facilityId, effectiveDate, notes.trim(), clause.trim() || undefined);
      setOpen(false); setNotes(''); setClause('');
      onFeedback({ tone: 'success', message: `Term-out effective ${date(effectiveDate)} requested; a different checker approves it.` });
      await load();
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The term-out could not be requested.') });
    } finally { setBusy(false); }
  };
  const decide = async (row: TermOutRequestRow, approve: boolean) => {
    setBusy(true);
    try {
      await decideTermOut(facilityId, row.id, approve, approve ? 'Approved' : 'Rejected');
      onFeedback({ tone: 'success', message: approve
        ? `Term-out approved: the revolving period ends ${date(row.effectiveFrom)}; no new draws after it and the unused fee stops.`
        : 'Term-out rejected; the facility keeps revolving.' });
      await load();
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The term-out could not be decided.') });
    } finally { setBusy(false); }
  };

  if (!phase) return null;
  const pending = rows.find(r => r.status === 'PENDING_APPROVAL');
  const canRequest = canMake && !pending && phase.code !== 'TERMED_OUT' && phase.code !== 'MATURED';
  return (
    <Card aria-label="Facility structure and term-out">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h3>Facility structure</h3>
          <p className="sub">{phaseLabel(phase.code)}{phaseDates(phase) ? ` · ${phaseDates(phase)}` : ''} · {phase.basis}</p>
        </div>
        {canRequest && !open && <button type="button" onClick={() => setOpen(true)}>Request term-out</button>}
      </div>
      <div className="grid">
        <div className="fact"><div className="label">Commitment type</div><div className="value">{words(phase.commitmentType)}</div><div className="muted" style={{ fontSize: 11 }}>{phase.reborrowAllowed ? 'repaid amounts may be redrawn' : 'no reborrowing'}</div></div>
        <div className="fact"><div className="label">Revolving period end</div><div className="value">{date(phase.revolvingPeriodEnd)}</div><div className="muted" style={{ fontSize: 11 }}>{phase.revolvingPeriodEnd ? 'commitment termination date' : 'not stated in the agreement'}</div></div>
        <div className="fact"><div className="label">Amortization from</div><div className="value">{date(phase.amortizationStartDate)}</div><div className="muted" style={{ fontSize: 11 }}>to maturity {date(phase.maturityDate)}</div></div>
        <div className="fact"><div className="label">Fees on undrawn commitment</div><div className="value">{phase.unusedFeeBps != null ? `${phase.unusedFeeBps} bps unused` : '—'}</div><div className="muted" style={{ fontSize: 11 }}>{phase.tickingFeeBps ? `${phase.tickingFeeBps} bps ticking during ramp-up` : phase.drawsPermitted ? 'accrues while the commitment can be drawn' : 'stopped: the commitment can no longer be drawn'}</div></div>
      </div>
      {open && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Effective date
          <input aria-label="Term-out effective date" type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Source clause
          <input aria-label="Term-out source clause" value={clause} onChange={e => setClause(e.target.value)} placeholder="e.g. §2.08 Term-Out Option" /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11, flex: '1 1 260px' }}>Rationale
          <input aria-label="Term-out rationale" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why the facility terms out on this date" /></label>
        <button type="button" className="primary" disabled={busy || !effectiveDate || !notes.trim()} onClick={() => void submit()}>{busy ? 'Requesting…' : 'Request'}</button>
        <button type="button" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>}
      {rows.length > 0 && <table>
        <thead><tr><th>Effective</th><th>Status</th><th>Requested</th><th>Decided</th><th>Notes</th><th /></tr></thead>
        <tbody>{rows.map(r => {
          const ownRequest = !!currentUser && !!r.requestedBy && r.requestedBy.toLowerCase() === currentUser.toLowerCase();
          const decidable = r.status === 'PENDING_APPROVAL' && canCheck && !ownRequest;
          return (
            <tr key={r.id}>
              <td>{date(r.effectiveFrom)}</td>
              <td>{words(r.status)}</td>
              <td className="muted">{r.requestedBy || '—'}<br />{r.requestedAt ? fmtDate(r.requestedAt) : ''}</td>
              <td className="muted">{r.decidedBy || '—'}<br />{r.decidedAt ? fmtDate(r.decidedAt) : ''}</td>
              <td className="muted">{r.notes}{r.decisionNotes ? ` · ${r.decisionNotes}` : ''}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.status === 'PENDING_APPROVAL' && (decidable
                  ? <><button type="button" className="primary" disabled={busy} onClick={() => void decide(r, true)}>Approve</button>{' '}
                      <button type="button" disabled={busy} onClick={() => void decide(r, false)}>Reject</button></>
                  : <span className="muted">{ownRequest ? 'a different checker decides' : canCheck ? '' : 'awaiting a checker'}</span>)}
              </td>
            </tr>
          );
        })}</tbody>
      </table>}
      <div className="boundary">The phase is derived from the contract version in force and the business date; nothing here edits the contract. A term-out needs a maker and a different checker. After it the draw gate refuses new money, the unused fee stops and the facility amortises to maturity.</div>
    </Card>
  );
};

export default TermOutPanel;
